/**
 * Utility functions for parsing and handling names
 */

export interface ParsedName {
  firstName: string;
  lastName?: string;
}

/**
 * Intelligently splits a full name into first and last name
 */
export function parseFullName(fullName: string): ParsedName {
  if (!fullName || typeof fullName !== 'string') {
    return { firstName: '' };
  }

  const trimmed = fullName.trim();
  if (!trimmed) {
    return { firstName: '' };
  }

  // Split by spaces and filter out empty strings
  const parts = trimmed.split(/\s+/).filter(part => part.length > 0);

  if (parts.length === 0) {
    return { firstName: '' };
  }

  if (parts.length === 1) {
    return { firstName: parts[0] };
  }

  // For multiple parts, take first as firstName and join rest as lastName
  const firstName = parts[0];
  const lastName = parts.slice(1).join(' ');

  return {
    firstName,
    lastName: lastName || undefined
  };
}

/**
 * Checks if a name appears to be a full name (contains space)
 */
export function isFullName(name: string): boolean {
  return typeof name === 'string' && name.trim().includes(' ');
}

/**
 * Gets display name for addressing in sessions (only first name)
 */
export function getDisplayNameForSession(firstName: string, preferredName?: string): string {
  if (preferredName && preferredName.trim()) {
    // If preferred name has spaces, use only the first word
    return preferredName.trim().split(/\s+/)[0];
  }
  
  // If firstName has spaces, use only the first word
  return firstName.trim().split(/\s+/)[0];
}